import React, { useMemo } from "react";
import { Calendar, Clock, FileText, CheckCircle } from "lucide-react";
import { addDaysToDate, DEFAULT_DUE_DAYS } from "../../hooks/useAppState";
import InvoiceStatusBadge from "./InvoiceStatusBadge";

// Formattazione italiana: 1.234,56 €
function formatCurrency(value) {
  const num = Number(value || 0);
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  const [intPart, decPart] = abs.toFixed(2).split(".");
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${groupedInt},${decPart} €`;
}

// Calculate days until date
function getDaysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  const diffTime = target - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export default function UpcomingDeadlines({
  invoices,
  selectedYear,
  fiscalCalc,
  darkMode = false,
}) {
  const today = new Date().toISOString().split("T")[0];
  const currentYear = new Date().getFullYear();

  // Scadenze fiscali per l'anno corrente
  const fiscalDeadlines = useMemo(() => {
    if (selectedYear !== currentYear) return [];

    const deadlines = [];
    const totaleTasse = fiscalCalc?.totaleTasse || 0;

    // Calcola importi acconti
    // Saldo anno precedente + primo acconto = 30 Giugno
    // Secondo acconto = 30 Novembre
    const saldoPrimoAcconto = totaleTasse * 0.5; // Stima semplificata
    const secondoAcconto = totaleTasse * 0.5;

    // 30 Giugno - Saldo + 1° acconto
    const giugnoDate = `${currentYear}-06-30`;
    if (giugnoDate >= today) {
      deadlines.push({
        id: "giugno",
        date: giugnoDate,
        label: "30 Giugno " + currentYear,
        description: "Saldo + 1° acconto",
        amount: saldoPrimoAcconto,
        daysUntil: getDaysUntil(giugnoDate),
      });
    }

    // 30 Novembre - 2° acconto
    const novembreDate = `${currentYear}-11-30`;
    if (novembreDate >= today) {
      deadlines.push({
        id: "novembre",
        date: novembreDate,
        label: "30 Novembre " + currentYear,
        description: "2° acconto",
        amount: secondoAcconto,
        daysUntil: getDaysUntil(novembreDate),
      });
    }

    return deadlines;
  }, [selectedYear, currentYear, fiscalCalc, today]);

  // Fatture in scadenza nei prossimi 30 giorni
  const upcomingInvoices = useMemo(() => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    const thirtyDaysStr = thirtyDaysFromNow.toISOString().split("T")[0];

    return invoices
      .filter((inv) => {
        if (inv.paid) return false;
        if (!inv.issueDate) return false;
        const year = new Date(inv.issueDate).getFullYear();
        if (year !== selectedYear) return false;

        const dueDate = inv.dueDate || addDaysToDate(inv.issueDate, DEFAULT_DUE_DAYS);
        // Fatture che scadono nei prossimi 30 giorni (non già scadute)
        return dueDate >= today && dueDate <= thirtyDaysStr;
      })
      .map((inv) => {
        const dueDate = inv.dueDate || addDaysToDate(inv.issueDate, DEFAULT_DUE_DAYS);
        return {
          ...inv,
          dueDate,
          daysUntil: getDaysUntil(dueDate),
        };
      })
      .sort((a, b) => a.daysUntil - b.daysUntil)
      .slice(0, 4);
  }, [invoices, selectedYear, today]);

  const hasDeadlines = fiscalDeadlines.length > 0 || upcomingInvoices.length > 0;

  if (!hasDeadlines) {
    return null; // Non mostrare la sezione se non ci sono scadenze
  }

  return (
    <div
      className={`
        rounded-xl border p-6
        transition-all duration-200
        ${darkMode
          ? "bg-neutral-900 border-neutral-800 shadow-md"
          : "bg-white border-neutral-200 shadow-sm"
        }
      `}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-5">
        <Calendar className={`w-5 h-5 ${darkMode ? "text-neutral-500" : "text-neutral-600"}`} />
        <h3 className={`text-sm font-medium ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
          Prossime scadenze
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Colonna sinistra: Scadenze fiscali */}
        <div>
          <h4 className={`text-xs font-semibold uppercase tracking-wide mb-3 ${
            darkMode ? "text-neutral-600" : "text-neutral-500"
          }`}>
            Fiscali
          </h4>

          {fiscalDeadlines.length > 0 ? (
            <div className="space-y-3">
              {fiscalDeadlines.map((deadline) => (
                <div
                  key={deadline.id}
                  className={`flex items-start gap-3 p-3 rounded-lg ${
                    darkMode ? "bg-neutral-800/50" : "bg-neutral-50"
                  }`}
                >
                  <div className={`p-2 rounded-lg shrink-0 ${
                    deadline.daysUntil <= 30
                      ? darkMode ? "bg-amber-900/30" : "bg-amber-100"
                      : darkMode ? "bg-neutral-700" : "bg-neutral-100"
                  }`}>
                    <Clock className={`w-4 h-4 ${
                      deadline.daysUntil <= 30
                        ? darkMode ? "text-amber-400" : "text-amber-600"
                        : darkMode ? "text-neutral-500" : "text-neutral-600"
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm font-medium ${
                        darkMode ? "text-white" : "text-neutral-900"
                      }`}>
                        {deadline.label}
                      </span>
                      <span className={`text-sm font-semibold ${
                        darkMode ? "text-white" : "text-neutral-900"
                      }`}>
                        {formatCurrency(deadline.amount)}
                      </span>
                    </div>
                    <p className={`text-xs mt-0.5 ${
                      darkMode ? "text-neutral-500" : "text-neutral-600"
                    }`}>
                      {deadline.description}
                    </p>
                    {deadline.daysUntil <= 30 && (
                      <span className={`inline-block mt-1.5 text-xs px-2 py-0.5 rounded-full ${
                        darkMode ? "bg-amber-900/30 text-amber-400" : "bg-amber-100 text-amber-700"
                      }`}>
                        Tra {deadline.daysUntil} giorni
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`flex items-center gap-2 p-3 rounded-lg ${
              darkMode ? "bg-neutral-800/50" : "bg-neutral-50"
            }`}>
              <CheckCircle className={`w-4 h-4 ${
                darkMode ? "text-emerald-400" : "text-emerald-600"
              }`} />
              <span className={`text-sm ${
                darkMode ? "text-neutral-500" : "text-neutral-600"
              }`}>
                Nessuna scadenza fiscale imminente
              </span>
            </div>
          )}
        </div>

        {/* Colonna destra: Fatture in scadenza */}
        <div>
          <h4 className={`text-xs font-semibold uppercase tracking-wide mb-3 ${
            darkMode ? "text-neutral-600" : "text-neutral-500"
          }`}>
            Fatture da incassare
          </h4>

          {upcomingInvoices.length > 0 ? (
            <div className="space-y-3">
              {upcomingInvoices.map((inv) => (
                <div
                  key={inv.id}
                  className={`flex items-start gap-3 p-3 rounded-lg ${
                    darkMode ? "bg-neutral-800/50" : "bg-neutral-50"
                  }`}
                >
                  <div className={`p-2 rounded-lg shrink-0 ${
                    inv.daysUntil <= 7
                      ? darkMode ? "bg-rose-900/30" : "bg-rose-100"
                      : darkMode ? "bg-neutral-700" : "bg-neutral-100"
                  }`}>
                    <FileText className={`w-4 h-4 ${
                      inv.daysUntil <= 7
                        ? darkMode ? "text-rose-400" : "text-rose-600"
                        : darkMode ? "text-neutral-500" : "text-neutral-600"
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm font-medium truncate ${
                        darkMode ? "text-white" : "text-neutral-900"
                      }`}>
                        {inv.clientName || "Cliente"}
                      </span>
                      <span className={`text-sm font-semibold shrink-0 ${
                        darkMode ? "text-white" : "text-neutral-900"
                      }`}>
                        {formatCurrency(inv.grossAmount)}
                      </span>
                    </div>
                    <p className={`text-xs mt-0.5 ${
                      darkMode ? "text-neutral-500" : "text-neutral-600"
                    }`}>
                      {inv.invoiceCode}
                    </p>
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <InvoiceStatusBadge status="pending" darkMode={darkMode} size="sm" />
                      <span className={`text-xs font-medium ${
                        inv.daysUntil <= 7
                          ? darkMode ? "text-rose-400" : "text-rose-600"
                          : darkMode ? "text-neutral-500" : "text-neutral-600"
                      }`}>
                        {inv.daysUntil === 0
                          ? "Scade oggi"
                          : inv.daysUntil === 1
                          ? "Scade domani"
                          : `Tra ${inv.daysUntil} giorni`}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`flex items-center gap-2 p-3 rounded-lg ${
              darkMode ? "bg-neutral-800/50" : "bg-neutral-50"
            }`}>
              <CheckCircle className={`w-4 h-4 ${
                darkMode ? "text-emerald-400" : "text-emerald-600"
              }`} />
              <span className={`text-sm ${
                darkMode ? "text-neutral-500" : "text-neutral-600"
              }`}>
                Nessuna fattura in scadenza nei prossimi 30 giorni
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
